/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, Download, CheckCircle2, Zap, Apple, Monitor, Package as PackageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useEffect, useState } from 'react';
import { DEFAULT_VERSION } from '@shared/config/version';

interface Platform {
  name: string;
  icon: React.ReactNode;
  description: string;
  downloadUrl: string;
  fileSize: string;
  requirements: string;
}

interface VersionInfo {
  version: string;
  releaseDate: string;
}

// TODO: Currently using test bucket as production. When migrating to production:
// 1. Create gs://velocity-downloads bucket
// 2. Restore environment-based bucket selection:
//    const OBIN_BUCKET_BASE = 'https://storage.googleapis.com/velocity-downloads/obin';
//    const OBIN_TEST_BUCKET_BASE = 'https://storage.googleapis.com/velocity-test-downloads/obin';
//    const bucketBase = versionInfo?.environment === 'production' ? OBIN_BUCKET_BASE : OBIN_TEST_BUCKET_BASE;
// See: product/docs/MARKETING_WEBSITE.md for full migration checklist
const OBIN_BUCKET_BASE = 'https://storage.googleapis.com/velocity-test-downloads/obin';

export function OBinDownloadPage() {
  const [detectedPlatform, setDetectedPlatform] = useState<'mac' | 'windows' | 'linux'>('mac');
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.includes('mac')) {
      setDetectedPlatform('mac');
    } else if (userAgent.includes('win')) {
      setDetectedPlatform('windows');
    } else if (userAgent.includes('linux')) {
      setDetectedPlatform('linux');
    }

    const fetchVersionInfo = async () => {
      try {
        const response = await fetch('/api/version');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setVersionInfo(data.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch version info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVersionInfo();
  }, []);

  const version = versionInfo?.version || DEFAULT_VERSION;
  const bucketBase = OBIN_BUCKET_BASE;
  
  const platforms: Record<string, Platform> = {
    mac: {
      name: 'macOS',
      icon: <Apple className="h-8 w-8" />,
      description: 'For Apple Silicon (M1-M5) Macs',
      downloadUrl: `${bucketBase}/velocity-latest-mac-arm64.dmg`,
      fileSize: '~260 MB',
      requirements: 'macOS 11.0 (Big Sur) or later',
    },
    windows: {
      name: 'Windows',
      icon: <Monitor className="h-8 w-8" />,
      description: 'For Windows 10 and Windows 11',
      downloadUrl: `${bucketBase}/velocity-latest-windows-x64.exe`,
      fileSize: '~190 MB',
      requirements: 'Windows 10 version 1809 or later',
    },
    linux: {
      name: 'Linux',
      icon: <PackageIcon className="h-8 w-8" />,
      description: 'AppImage for all Linux distributions',
      downloadUrl: `${bucketBase}/velocity-latest-linux-x86_64.AppImage`,
      fileSize: '~110 MB',
      requirements: 'Most modern Linux distributions',
    },
  };

  const recommendedPlatform = platforms[detectedPlatform];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src={`${import.meta.env.BASE_URL}assets/obin_logo.png`}
                alt="Obin" 
                className="h-8 w-auto"
              />
              <span className="text-slate-400">×</span>
              <div className="flex items-center space-x-2">
                <Zap className="h-6 w-6 text-blue-600" />
                <span className="text-xl font-bold text-slate-900">Velocity</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/home" className="text-slate-600 hover:text-slate-900 transition-colors">
                <Button variant="ghost">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-4 mb-6">
            <img 
              src={`${import.meta.env.BASE_URL}assets/obin_logo.png`}
              alt="Obin" 
              className="h-12 w-auto"
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Download Velocity for Obin
          </h1>
          <p className="text-xl text-slate-600 mb-4">
            Get the Obin-branded desktop app for orchestrating AI agents across your product lifecycle
          </p>
          <p className="text-sm text-slate-500 bg-slate-100 inline-block px-4 py-2 rounded-full">
            This is the Obin partner version of Velocity PPM
          </p>
        </div>
      </section>

      {/* Recommended Download */}
      <section className="pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#0a1628] to-[#1e3a5f] rounded-2xl p-8 md:p-12 text-white shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-blue-200 mb-2">Recommended for your system</p>
                <div className="flex items-center space-x-3">
                  {recommendedPlatform.icon}
                  <h2 className="text-3xl font-bold">{recommendedPlatform.name}</h2>
                </div>
              </div>
              <div className="hidden md:block">
                <Download className="h-16 w-16 text-blue-300" />
              </div>
            </div>
            <p className="text-blue-100 mb-6">{recommendedPlatform.description}</p>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-6">
              <a href={recommendedPlatform.downloadUrl} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-[#0a1628] hover:bg-blue-50 text-lg px-8 py-6">
                  <Download className="mr-2 h-5 w-5" />
                  Download for {recommendedPlatform.name}
                </Button>
              </a>
              <div className="text-sm text-blue-100">
                <p>Version {version} • {recommendedPlatform.fileSize}</p>
                <p>{recommendedPlatform.requirements}</p>
              </div>
            </div>
            <div className="border-t border-blue-400/30 pt-6">
              <p className="text-sm text-blue-100">
                This Obin-branded version includes the full Agentic SDLC toolkit with Obin branding and customizations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* All Platforms */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Download for other platforms
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(platforms).map(([key, platform]) => (
              <div
                key={key}
                className={`bg-white rounded-xl p-6 border-2 transition-all hover:shadow-lg ${
                  key === detectedPlatform
                    ? 'border-blue-300 bg-blue-50/50'
                    : 'border-slate-200 hover:border-blue-200'
                }`}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="text-slate-700">{platform.icon}</div>
                  <h3 className="text-xl font-bold text-slate-900">{platform.name}</h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">{platform.description}</p>
                <div className="text-xs text-slate-500 mb-4">
                  <p>{platform.fileSize}</p>
                  <p>{platform.requirements}</p>
                </div>
                <a href={platform.downloadUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <Button
                    variant={key === detectedPlatform ? 'default' : 'outline'}
                    className="w-full"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </Button>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Instructions */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Installation Instructions
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* macOS */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center space-x-2 mb-4">
                <Apple className="h-6 w-6 text-slate-700" />
                <h3 className="text-xl font-bold text-slate-900">macOS</h3>
              </div>
              <ol className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">1.</span>
                  <span>Download the .dmg file</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">2.</span>
                  <span>Open the downloaded file</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">3.</span>
                  <span>Drag Velocity to Applications</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">4.</span>
                  <span>Launch from Applications folder</span>
                </li>
              </ol>
            </div>

            {/* Windows */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center space-x-2 mb-4">
                <Monitor className="h-6 w-6 text-slate-700" />
                <h3 className="text-xl font-bold text-slate-900">Windows</h3>
              </div>
              <ol className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">1.</span>
                  <span>Download the .exe installer</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">2.</span>
                  <span>Run the installer</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">3.</span>
                  <span>Follow the setup wizard</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">4.</span>
                  <span>Launch from Start Menu</span>
                </li>
              </ol>
            </div>

            {/* Linux */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center space-x-2 mb-4">
                <PackageIcon className="h-6 w-6 text-slate-700" />
                <h3 className="text-xl font-bold text-slate-900">Linux</h3>
              </div>
              <ol className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">1.</span>
                  <span>Download the .AppImage file</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">2.</span>
                  <span>Make it executable: chmod +x</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">3.</span>
                  <span>Run the AppImage</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">4.</span>
                  <span>Optional: Integrate with system</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            What's included
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'AI-powered development agents',
              'Velocity Playbook System for context engineering',
              'Distributed System Agents for oversight',
              'MCP server for Cursor & Claude Code integration',
              'Offline mode support',
              'Local repository integration',
              'Multi-product portfolio management',
              'Automated security and architecture audits',
            ].map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-blue-50 rounded-xl p-8 border border-blue-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Need help?</h3>
            <p className="text-slate-600 mb-6">
              Check out the documentation or contact your Obin representative if you encounter any issues during installation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="outline">
                View Documentation
              </Button>
              <Button variant="outline">
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <img 
                src={`${import.meta.env.BASE_URL}assets/obin_logo.png`}
                alt="Obin" 
                className="h-6 w-auto brightness-200"
              />
              <span className="text-slate-500">×</span>
              <div className="flex items-center space-x-2">
                <Zap className="h-6 w-6 text-blue-500" />
                <span className="text-xl font-bold text-white">Velocity</span>
              </div>
            </div>
            <p className="text-sm">
              &copy; 2025 Lumavec Group. All rights reserved.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Velocity for Obin is a white-label version of Velocity PPM
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
